import React, { useState, useEffect, useRef } from 'react';
import { Edit2, X } from 'lucide-react';
import { supabase } from '../../services/supabase';
import { DOC_TYPES, TABLE_NAMES } from '../../constants';
import JournalEntryMetaFields from './JournalEntryMetaFields';
import JournalContentEditor from './JournalContentEditor';
import { getChangelog } from '../../utils/journalPosts';

const EditPostForm = ({ post, isAdmin, setMessage, onPostUpdated, onCancel, fullPane }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [imageUrls, setImageUrls] = useState(['']);
  const [docType, setDocType] = useState(DOC_TYPES.POST);
  const [changelog, setChangelog] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const contentEditorRef = useRef(null);

  useEffect(() => {
    if (post) {
      setTitle(post.title || '');
      setContent(post.content || '');
      setTags(post.tags?.join(', ') || '');
      setDocType(post.doc_type || DOC_TYPES.POST);
      setChangelog(getChangelog(post));
      if (post.image_urls && post.image_urls.length > 0) {
        setImageUrls(post.image_urls);
      } else if (post.image_url) {
        setImageUrls([post.image_url]);
      } else {
        setImageUrls(['']);
      }
    }
  }, [post]);

  const handleAddImageField = () => {
    setImageUrls([...imageUrls, '']);
  };

  const handleRemoveImageField = (index) => {
    setImageUrls(imageUrls.filter((_, i) => i !== index));
  };

  const handleImageUrlChange = (index, value) => {
    const newUrls = [...imageUrls];
    newUrls[index] = value;
    setImageUrls(newUrls);
  };

  if (!isAdmin || !post) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = contentEditorRef.current?.flush?.() ?? content;
    if (!title || !body) return;

    try {
      const validImageUrls = imageUrls
        .map(url => url.trim())
        .filter(url => url.length > 0);

      const cleanedChangelog = changelog
        .filter((entry) => entry.date || entry.note?.trim())
        .map((entry) => ({ date: entry.date, note: entry.note.trim() }));

      const { error } = await supabase
        .from(TABLE_NAMES.BLOG_POSTS)
        .update({
          title,
          content: body,
          summary: body.substring(0, 150) + '...',
          tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0),
          image_urls: validImageUrls.length > 0 ? validImageUrls : null,
          updated_at: new Date().toISOString(),
          doc_type: docType,
          changelog: docType === DOC_TYPES.LIVING ? cleanedChangelog : [],
        })
        .eq('id', post.id);

      if (error) throw error;
      setMessage("Journal entry updated!");
      setIsEditing(false);
      if (onPostUpdated) onPostUpdated();
    } catch (error) {
      console.error("Error updating entry: ", error);
      setMessage("Failed to update entry.");
    }
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this journal entry? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from(TABLE_NAMES.BLOG_POSTS)
        .delete()
        .eq('id', post.id);

      if (error) throw error;
      setMessage("Journal entry deleted!");
      if (onPostUpdated) onPostUpdated();
    } catch (error) {
      console.error("Error deleting entry: ", error);
      setMessage("Failed to delete entry.");
    }
    setTimeout(() => setMessage(''), 3000);
  };

  if (!isEditing) {
    return (
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setIsEditing(true)}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-1"
        >
          <Edit2 className="w-4 h-4" />
          Edit
        </button>
        <button
          onClick={handleDelete}
          className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-1"
        >
          <X className="w-4 h-4" />
          Delete
        </button>
      </div>
    );
  }

  return (
    <div className={`p-6 bg-blue-50 rounded-xl shadow-lg border border-blue-200 space-y-4 mb-6 ${fullPane ? 'min-h-[60vh]' : ''}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-blue-800 flex items-center">
          <Edit2 className="w-5 h-5 mr-2" />
          Admin: Edit Journal Entry
        </h3>
        <button
          onClick={() => {
            setIsEditing(false);
            if (onCancel) onCancel();
          }}
          className="text-gray-600 hover:text-gray-800"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="field"
          required
        />
        <input
          type="text"
          placeholder="Tags (comma separated, e.g., React, Coding)"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          className="field"
        />
        <JournalEntryMetaFields
          docType={docType}
          setDocType={setDocType}
          changelog={changelog}
          setChangelog={setChangelog}
          accent="blue"
        />
        <div className="space-y-2">
          <label className="block text-sm font-medium text-blue-800">Image URLs (optional)</label>
          {imageUrls.map((url, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="url"
                placeholder={`Image URL ${index + 1}`}
                value={url}
                onChange={(e) => handleImageUrlChange(index, e.target.value)}
                className="field flex-1"
              />
              {imageUrls.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveImageField(index)}
                  className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddImageField}
            className="w-full py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition"
          >
            + Add Another Image
          </button>
        </div>
        <JournalContentEditor
          ref={contentEditorRef}
          value={content}
          onChange={setContent}
          rows={fullPane ? 24 : 14}
          accent="blue"
        />
        <div className="flex gap-2">
          <button
            type="submit"
            className="flex-1 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
          >
            Update Entry
          </button>
          <button
            type="button"
            onClick={() => {
              setIsEditing(false);
              if (onCancel) onCancel();
            }}
            className="px-4 py-2 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPostForm;
